import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { faCircleNotch, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FaIcon } from "@shared/client/components/fa-icon";
import { showToast } from "@shared/client/components/toast/show-toast";
import { MAX_NAME_SLUG_LENGTH, MIN_NAME_SLUG_LENGTH, isValidNameSlug, normalizeNameSlug, } from "@shared/types/slug";
import { isReservedRootName } from "@shared/types/reserved-names";
import { claimUsername } from "@/api/command-api";
import { ACCOUNT_ME_QUERY_KEY } from "@/hooks/use-account-me";
import "./username-claim-modal.scss";
/**
 * Modal that prompts the authenticated user to claim a root-namespace
 * username. Mirrors the visual idiom of `CollectionSettingsModal` for
 * consistency — backdrop with click-to-close, centered card, 8px-radius
 * primary button.
 *
 * Validation runs client-side against the same constraints the server
 * enforces (`@shared/types/slug` `NAME_SLUG_PATTERN` + reserved-name list)
 * so common errors surface inline before the round-trip.
 */
export const UsernameClaimModal = memo(({ account, contextMessage, onClose, onClaimed, allowSkip = true, }) => {
    const inputRef = useRef(null);
    const queryClient = useQueryClient();
    const [value, setValue] = useState("");
    const hasUsername = account?.username !== undefined;
    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape")
                onClose();
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);
    useEffect(() => {
        if (hasUsername)
            return;
        if (typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches) {
            return;
        }
        inputRef.current?.focus();
    }, [hasUsername]);
    const claimMutation = useMutation({
        mutationFn: (username) => claimUsername(username),
        onSuccess: (claimed) => {
            // Push the freshly-claimed row directly into the cache instead
            // of invalidating: `useAccountMe`'s queryFn awaits the bootstrap
            // singleton, which is a frozen snapshot of cold-load state and
            // would replay the OLD account row on a refetch. `setQueryData`
            // updates every subscriber (account menu, author filter,
            // share-flow guard) in the same React commit without a network
            // round-trip.
            queryClient.setQueryData(ACCOUNT_ME_QUERY_KEY, claimed);
            showToast.success(`Username claimed: @${claimed.username ?? value}`);
            if (claimed.username !== undefined) {
                onClaimed?.({ id: claimed.id, username: claimed.username });
            }
            onClose();
        },
        onError: (err) => {
            showToast.error(err instanceof Error ? err.message : "Failed to claim username");
        },
    });
    const validation = useMemo(() => validateCandidate(value), [value]);
    const handleBackdropClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    }, [onClose]);
    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        if (validation.kind !== "ok")
            return;
        claimMutation.mutate(validation.normalized);
    }, [validation, claimMutation]);
    return (_jsx("div", { className: "username-claim-modal__backdrop", role: "dialog", "aria-modal": "true", "aria-labelledby": "username-claim-modal-title", onMouseDown: handleBackdropClick, children: _jsxs("div", { className: "username-claim-modal", children: [_jsxs("header", { className: "username-claim-modal__header", children: [_jsx("h2", { id: "username-claim-modal-title", className: "username-claim-modal__heading", children: hasUsername ? "Your username" : "Claim a username" }), _jsx("button", { type: "button", className: "username-claim-modal__close", onClick: onClose, "aria-label": "Close", children: _jsx(FaIcon, { icon: faXmark }) })] }), _jsxs("section", { className: "username-claim-modal__section", children: [contextMessage ? (_jsx("p", { className: "username-claim-modal__context", children: contextMessage })) : null, hasUsername ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "username-claim-modal__handle-row", children: _jsxs("span", { className: "username-claim-modal__handle", children: ["@", account?.username] }) }), _jsx("p", { className: "username-claim-modal__hint", children: "Renaming will be available soon. For now, this is your handle on Putty." })] })) : (_jsxs("form", { className: "username-claim-modal__form", onSubmit: handleSubmit, children: [_jsxs("div", { className: "username-claim-modal__input-wrap", children: [_jsx("span", { className: "username-claim-modal__input-prefix", children: "@" }), _jsx("input", { ref: inputRef, type: "text", className: "username-claim-modal__input", value: value, onChange: (e) => setValue(e.target.value), placeholder: "your-handle", maxLength: MAX_NAME_SLUG_LENGTH, autoCapitalize: "none", autoCorrect: "off", spellCheck: false, "aria-label": "Username", disabled: claimMutation.isPending })] }), _jsx("p", { className: validation.kind === "error"
                                        ? "username-claim-modal__hint username-claim-modal__hint--error"
                                        : "username-claim-modal__hint", children: validation.kind === "error"
                                        ? validation.message
                                        : `${MIN_NAME_SLUG_LENGTH}–${MAX_NAME_SLUG_LENGTH} chars: lowercase letters, digits, hyphens. Powers your putty.me/{name} URL.` }), _jsxs("div", { className: "username-claim-modal__actions", children: [allowSkip ? (_jsx("button", { type: "button", className: "username-claim-modal__secondary-btn", onClick: onClose, disabled: claimMutation.isPending, children: "Maybe later" })) : null, _jsx("button", { type: "submit", className: "username-claim-modal__primary-btn", disabled: validation.kind !== "ok" || claimMutation.isPending, children: claimMutation.isPending ? (_jsx(FaIcon, { icon: faCircleNotch, spin: true })) : ("Claim") })] })] }))] })] }) }));
});
function validateCandidate(raw) {
    const trimmed = raw.trim();
    if (trimmed.length === 0)
        return { kind: "empty" };
    const normalized = normalizeNameSlug(trimmed);
    if (normalized.length < MIN_NAME_SLUG_LENGTH) {
        return {
            kind: "error",
            message: `Too short — must be at least ${MIN_NAME_SLUG_LENGTH} characters.`,
        };
    }
    if (normalized.length > MAX_NAME_SLUG_LENGTH) {
        return {
            kind: "error",
            message: `Too long — must be at most ${MAX_NAME_SLUG_LENGTH} characters.`,
        };
    }
    if (isValidNameSlug(normalized) === false) {
        return {
            kind: "error",
            message: "Only lowercase letters, digits, and hyphens. No leading/trailing/consecutive hyphens.",
        };
    }
    if (isReservedRootName(normalized)) {
        return {
            kind: "error",
            message: `"${normalized}" is reserved — try another.`,
        };
    }
    return { kind: "ok", normalized };
}
